import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import {
  Circle,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Trash2,
  Play,
  Plus,
  ChevronDown,
  Calendar,
  AlertCircle,
  Timer,
  Zap,
  Activity,
  Target,
  LayoutList,
  LayoutGrid,
  Rows3,
  CreditCard,
  FolderKanban,
  FolderOpen,
  FolderClosed,
  FolderPlus,
  Pencil,
  Filter,
  X,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Tag,
  RotateCcw,
} from 'lucide-react';
import {
  useTaskingBotTasks,
  useUpdateTask,
  useDeleteTask,
  useExecuteTaskingBotTaskNow,
  useCreateTaskingBotTask,
  type TaskingBotTask,
} from '../../helpers/useTaskingBotTasks';
import { useTaskingBotProjects, useCreateProject, useUpdateProject, useDeleteProject, type TaskingBotProject } from '../../helpers/useTaskingBotProjects';
import { useTaskingBotCategories, useCreateCategory } from '../../helpers/useTaskingBotCategories';
import { TaskDetailModal } from '../TaskDetailModal';
import { toast } from 'sonner';
import styles from './TaskFlow.module.css';

// ─── Status config ────────────────────────────────────────
const COLUMNS = [
  { key: 'open', label: 'To Do', color: '#6b7280', dotColor: '#9ca3af' },
  { key: 'in_progress', label: 'In Progress', color: '#f59e0b', dotColor: '#f59e0b' },
  { key: 'completed', label: 'Done', color: '#22c55e', dotColor: '#22c55e' },
] as const;

const STATUS_BADGE: Record<string, { label: string; color: string; bg: string }> = {
  open: { label: 'To Do', color: '#9ca3af', bg: 'rgba(156,163,175,0.12)' },
  in_progress: { label: 'In Progress', color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
  completed: { label: 'Done', color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
  running: { label: 'Running', color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
  waiting_for_input: { label: 'Blocked', color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#6b7280',
};
const PRIORITY_LABELS: Record<string, string> = {
  urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low',
};

// Default project colors for tasks without a project color
const PROJECT_PALETTE = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];
function getProjectColor(project: TaskingBotProject | undefined): string {
  if (!project) return '#6b7280';
  if (project.color) return project.color;
  // Stable color from project ID
  let hash = 0;
  for (let i = 0; i < project.id.length; i++) hash = (hash * 31 + project.id.charCodeAt(i)) | 0;
  return PROJECT_PALETTE[Math.abs(hash) % PROJECT_PALETTE.length];
}

// ─── Parse task tags ──────────────────────────────────────
function parseTags(task: TaskingBotTask): string[] {
  try {
    if (!task.tags) return [];
    const parsed = JSON.parse(task.tags);
    return Array.isArray(parsed) ? parsed.filter((t: any) => typeof t === 'string' && t.trim()) : [];
  } catch { return []; }
}

const TAG_COLORS = ['#6366f1', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#f43f5e', '#a855f7', '#22d3ee'];
function getTagColor(tag: string): string {
  let hash = 0;
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) | 0;
  return TAG_COLORS[Math.abs(hash) % TAG_COLORS.length];
}

// ─── Parse task metadata ──────────────────────────────────
function parseTaskMeta(task: TaskingBotTask) {
  try {
    const meta = task.metadata ? JSON.parse(task.metadata) : {};
    const autonomous = meta?.autonomous || {};
    return {
      lastRunStatus: autonomous.lastRunStatus as string | null,
      lastRunError: autonomous.lastRunError as string | null,
      lastRunSummary: autonomous.lastRunSummary as string | null,
      lastRunAt: autonomous.lastRunAt as string | null,
      runCount: autonomous.runCount as number | undefined,
      tokensUsed: autonomous.tokensUsed as number | undefined,
      progress: (autonomous.progress ?? meta.progress) as number | undefined,
      currentStep: autonomous.currentStep as string | null,
      blockers: (meta?.blockers || []) as string[],
      notes: (meta?.notes || '') as string,
      subtasks: (meta?.subtasks || []) as Array<{ id: string; title: string; completed?: boolean; status?: string; completionSummary?: string }>,
    };
  } catch {
    return {
      lastRunStatus: null, lastRunError: null, lastRunSummary: null, lastRunAt: null,
      runCount: undefined, tokensUsed: undefined, progress: undefined, currentStep: null,
      blockers: [], notes: '', subtasks: [] as Array<{ id: string; title: string; completed?: boolean; status?: string; completionSummary?: string }>,
    };
  }
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

// ─── Project Tag ──────────────────────────────────────────
function ProjectTag({ project, size = 'sm' }: { project?: TaskingBotProject; size?: 'sm' | 'xs' }) {
  if (!project) return null;
  const color = getProjectColor(project);
  const fontSize = size === 'xs' ? '9px' : '10px';
  const padding = size === 'xs' ? '1px 5px' : '2px 7px';
  return (
    <span className={styles.projectTag} style={{ color, background: `${color}14`, borderColor: `${color}30`, fontSize, padding }}>
      <FolderKanban size={size === 'xs' ? 8 : 9} />
      {project.name}
    </span>
  );
}

// ─── Project Assign Dropdown ──────────────────────────────
function ProjectAssignDropdown({ task, projects, categories, onAssign }: {
  task: TaskingBotTask;
  projects: TaskingBotProject[];
  categories?: { id: string; name: string; position: number }[];
  onAssign: (taskId: string, projectId: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Group projects by category
  const grouped = useMemo(() => {
    if (!categories || categories.length === 0) return null;
    const map = new Map<string, TaskingBotProject[]>();
    const uncategorized: TaskingBotProject[] = [];
    for (const p of projects) {
      const catId = (p as any).categoryId;
      if (catId && categories.some(c => c.id === catId)) {
        const bucket = map.get(catId) || [];
        bucket.push(p);
        map.set(catId, bucket);
      } else {
        uncategorized.push(p);
      }
    }
    return { map, uncategorized };
  }, [projects, categories]);

  return (
    <div className={styles.projectDropdownWrap} ref={ref}>
      <button
        className={styles.projectDropdownTrigger}
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        title="Assign to project"
      >
        <FolderKanban size={11} />
        <ChevronDown size={9} />
      </button>
      {open && (
        <div className={styles.projectDropdownMenu} onClick={(e) => e.stopPropagation()}>
          <button
            className={`${styles.projectDropdownItem} ${!task.projectId ? styles.projectDropdownItemActive : ''}`}
            onClick={() => { onAssign(task.id, null); setOpen(false); }}
          >
            <span style={{ color: 'var(--muted-foreground)' }}>No project</span>
          </button>
          {grouped ? (
            <>
              {(categories || []).sort((a, b) => a.position - b.position).map(cat => {
                const catProjects = grouped.map.get(cat.id) || [];
                if (catProjects.length === 0) return null;
                return (
                  <React.Fragment key={cat.id}>
                    <div style={{ padding: '4px 8px', fontSize: '0.65rem', fontWeight: 600, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.7 }}>
                      {cat.name}
                    </div>
                    {catProjects.map(p => (
                      <button
                        key={p.id}
                        className={`${styles.projectDropdownItem} ${task.projectId === p.id ? styles.projectDropdownItemActive : ''}`}
                        onClick={() => { onAssign(task.id, p.id); setOpen(false); }}
                        style={{ paddingLeft: 16 }}
                      >
                        <span className={styles.projectDropdownDot} style={{ background: getProjectColor(p) }} />
                        {p.name}
                      </button>
                    ))}
                  </React.Fragment>
                );
              })}
              {grouped.uncategorized.map(p => (
                <button
                  key={p.id}
                  className={`${styles.projectDropdownItem} ${task.projectId === p.id ? styles.projectDropdownItemActive : ''}`}
                  onClick={() => { onAssign(task.id, p.id); setOpen(false); }}
                >
                  <span className={styles.projectDropdownDot} style={{ background: getProjectColor(p) }} />
                  {p.name}
                </button>
              ))}
            </>
          ) : (
            projects.map(p => (
              <button
                key={p.id}
                className={`${styles.projectDropdownItem} ${task.projectId === p.id ? styles.projectDropdownItemActive : ''}`}
                onClick={() => { onAssign(task.id, p.id); setOpen(false); }}
              >
                <span className={styles.projectDropdownDot} style={{ background: getProjectColor(p) }} />
                {p.name}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Confirm Dialog ──────────────────────────────────────
function ConfirmDialog({ title, message, onConfirm, onCancel }: {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onConfirm, onCancel]);

  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
        <h4 className={styles.confirmTitle}>{title}</h4>
        <p className={styles.confirmMessage}>{message}</p>
        <div className={styles.confirmActions}>
          <button className={styles.confirmCancel} onClick={onCancel}>Cancel</button>
          <button className={styles.confirmDelete} onClick={onConfirm}>
            <Trash2 size={12} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Donut Chart (SVG) ───────────────────────────────────
function DonutChart({ segments, size = 80, stroke = 10 }: {
  segments: { value: number; color: string; label: string }[];
  size?: number;
  stroke?: number;
}) {
  const total = segments.reduce((s, seg) => s + seg.value, 0);
  if (total === 0) return null;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  let offset = 0;

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      {segments.filter(s => s.value > 0).map((seg, i) => {
        const pct = seg.value / total;
        const dash = pct * circ;
        const el = (
          <circle
            key={i}
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={stroke}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            strokeLinecap="round"
            style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
          />
        );
        offset += dash;
        return el;
      })}
    </svg>
  );
}

// ─── Sparkline (SVG) ─────────────────────────────────────
function Sparkline({ data, color = '#22c55e', width = 120, height = 32 }: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
}) {
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth={1.5}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <polyline
        points={`0,${height} ${points} ${width},${height}`}
        fill={`${color}18`}
        stroke="none"
      />
    </svg>
  );
}

// ─── Stats Dashboard ─────────────────────────────────────
// ─── Area Chart with dots ────────────────────────────────
function useIsLightTheme() {
  const [light, setLight] = useState(() => {
    const t = document.documentElement.getAttribute('data-theme') || document.body.getAttribute('data-theme');
    return t === 'light';
  });
  useEffect(() => {
    const obs = new MutationObserver(() => {
      const t = document.documentElement.getAttribute('data-theme') || document.body.getAttribute('data-theme');
      setLight(t === 'light');
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return light;
}

function AreaChart({ data, color = '#22c55e', width = 160, height = 60, showDots = false, xLabels }: {
  data: number[];
  color?: string;
  width?: number;
  height?: number;
  showDots?: boolean;
  xLabels?: (string | number)[];
}) {
  const isLight = useIsLightTheme();
  if (data.length < 2) return null;
  const max = Math.max(...data, 1);
  const padTop = 6;
  const padBottom = xLabels ? 14 : 4;
  const chartH = height - padTop - padBottom;
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = padTop + chartH - (v / max) * chartH;
    return { x, y };
  });
  const linePoints = points.map(p => `${p.x},${p.y}`).join(' ');
  const areaPoints = `0,${padTop + chartH} ${linePoints} ${width},${padTop + chartH}`;

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {/* Grid lines */}
      {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
        <line key={i} x1={0} x2={width} y1={padTop + chartH * (1 - pct)} y2={padTop + chartH * (1 - pct)}
          stroke={isLight ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.06)'} strokeWidth={0.5} />
      ))}
      {/* Area fill */}
      <polygon points={areaPoints} fill={`url(#areaGrad-${color.replace('#', '')})`} />
      <defs>
        <linearGradient id={`areaGrad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {/* Line */}
      <polyline points={linePoints} fill="none" stroke={color} strokeWidth={1.8} strokeLinejoin="round" strokeLinecap="round" />
      {/* Dots */}
      {showDots && points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3} fill={color} stroke={isLight ? '#ffffff' : '#0a0f0a'} strokeWidth={1.5} />
      ))}
      {/* X-axis labels */}
      {xLabels && xLabels.map((label, i) => (
        <text key={i} x={(i / (xLabels.length - 1)) * width} y={height - 1}
          fill={isLight ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.35)'} fontSize={7} textAnchor="middle">{label}</text>
      ))}
    </svg>
  );
}

function StatsBar({ tasks }: { tasks: TaskingBotTask[] }) {
  const inProgress = tasks.filter(t => t.status === 'in_progress' || t.status === 'open').length;
  const completed = tasks.filter(t => t.status === 'completed').length;
  const total = tasks.length;
  const progressPct = total > 0 ? Math.round(((total - tasks.filter(t => t.status === 'open').length) / total) * 100) : 0;

  // Build 24-hour completion data (tasks completed per hour over last 24h)
  const completionData = useMemo(() => {
    const hours: number[] = new Array(8).fill(0);
    const now = Date.now();
    tasks.filter(t => t.status === 'completed').forEach(t => {
      const updated = t.updatedAt ? new Date(t.updatedAt).getTime() : 0;
      const hoursAgo = (now - updated) / (1000 * 60 * 60);
      if (hoursAgo < 24) {
        const bucket = Math.min(7, Math.floor((hoursAgo / 24) * 8));
        hours[7 - bucket]++;
      }
    });
    // Ensure some visual interest even with sparse data
    return hours.map(h => h || Math.random() * 0.3);
  }, [tasks]);

  // Build 7-day efficiency data
  const efficiencyData = useMemo(() => {
    const days: number[] = [];
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const dayTasks = tasks.filter(t => t.updatedAt?.slice(0, 10) === key);
      const dayCompleted = dayTasks.filter(t => t.status === 'completed').length;
      const dayTotal = dayTasks.length || 1;
      days.push(Math.round((dayCompleted / dayTotal) * 100));
    }
    return days;
  }, [tasks]);
  const avgEfficiency = efficiencyData.length > 0 ? Math.round(efficiencyData.reduce((a, b) => a + b, 0) / efficiencyData.length) : 0;

  // Completion trend (compare last 7 days to prior 7 days)
  const completionTrend = useMemo(() => {
    const now = Date.now();
    const recent = tasks.filter(t => {
      if (t.status !== 'completed') return false;
      const u = t.updatedAt ? new Date(t.updatedAt).getTime() : 0;
      return (now - u) < 7 * 24 * 60 * 60 * 1000;
    }).length;
    const prior = tasks.filter(t => {
      if (t.status !== 'completed') return false;
      const u = t.updatedAt ? new Date(t.updatedAt).getTime() : 0;
      const diff = now - u;
      return diff >= 7 * 24 * 60 * 60 * 1000 && diff < 14 * 24 * 60 * 60 * 1000;
    }).length;
    if (prior === 0) return recent > 0 ? 100 : 0;
    return Math.round(((recent - prior) / prior) * 100);
  }, [tasks]);

  const isLightTheme = useIsLightTheme();
  const donutSegments = [
    { value: progressPct, color: '#22c55e', label: 'Progress' },
    { value: 100 - progressPct, color: isLightTheme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)', label: 'Remaining' },
  ];

  return (
    <div className={styles.statsBar}>
      {/* Card 1: Active Tasks */}
      <div className={styles.statsCard}>
        <div className={styles.statsCardHeader}>
          <span className={styles.statsCardTitle}>Active Tasks <span className={styles.statsCardNum}>({inProgress})</span></span>
          <button className={styles.statsCardMenu}><span>&#8942;</span></button>
        </div>
        <div className={styles.statsCardBody}>
          <div className={styles.statsDonut}>
            <DonutChart segments={donutSegments} size={56} stroke={6} />
            <span className={styles.statsDonutPct}>{progressPct}%</span>
          </div>
        </div>
      </div>

      {/* Card 2: Completed */}
      <div className={styles.statsCard}>
        <div className={styles.statsCardHeader}>
          <span className={styles.statsCardTitle}>Completed <span className={styles.statsCardNum}>({completed})</span></span>
          {completionTrend !== 0 && (
            <span className={styles.statsTrend} style={{ color: completionTrend > 0 ? '#22c55e' : '#ef4444' }}>
              {completionTrend > 0 ? '+' : ''}{completionTrend}%
            </span>
          )}
        </div>
        <div className={styles.statsCardChart}>
          <AreaChart data={completionData} color="#22c55e" width={160} height={56} showDots
            xLabels={['0', '6', '12', '18', '24']} />
        </div>
      </div>

      {/* Card 3: Team Efficiency */}
      <div className={styles.statsCard}>
        <div className={styles.statsCardHeader}>
          <span className={styles.statsCardTitle}>Team Efficiency <span className={styles.statsCardNum}>({avgEfficiency}%)</span></span>
          <button className={styles.statsCardMenu}><span>&#8942;</span></button>
        </div>
        <div className={styles.statsCardChart}>
          <AreaChart data={efficiencyData} color="#22c55e" width={160} height={56} showDots
            xLabels={efficiencyData.map((_, i) => {
              const d = new Date();
              d.setDate(d.getDate() - (6 - i));
              return d.getDate();
            })} />
        </div>
      </div>
    </div>
  );
}

// ─── Subtask Traffic Light Dots ───────────────────────────
function SubtaskDots({ subtasks, onOpenDetail }: { subtasks: Array<{ id: string; title: string; completed?: boolean; status?: string }>; onOpenDetail?: (id: string) => void }) {
  if (subtasks.length === 0) return null;
  return (
    <div className={styles.subtaskDots} title={`${subtasks.filter(s => s.completed || s.status === 'completed').length}/${subtasks.length} subtasks completed`}>
      {subtasks.map(s => {
        const st = s.status || (s.completed ? 'completed' : 'open');
        const color = st === 'completed' ? '#22c55e' : st === 'in_progress' ? '#f59e0b' : '#ef4444';
        return (
          <span
            key={s.id}
            className={styles.subtaskDot}
            style={{ background: color, cursor: onOpenDetail ? 'pointer' : undefined }}
            title={`${s.title}: ${st}`}
            onClick={onOpenDetail ? (e) => { e.stopPropagation(); onOpenDetail(s.id); } : undefined}
          />
        );
      })}
    </div>
  );
}

// ─── Inline Editable Title ────────────────────────────────
function InlineEditableTitle({ task, className, runningIcon }: {
  task: TaskingBotTask;
  className?: string;
  runningIcon?: React.ReactNode;
}) {
  const updateTask = useUpdateTask();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(task.title);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(task.title);
    setEditing(true);
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const commit = () => {
    setEditing(false);
    const t = draft.trim();
    if (t && t !== task.title) {
      updateTask.mutate({ id: task.id, title: t } as any);
    } else {
      setDraft(task.title);
    }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        className={`${className || ''} ${styles.inlineTitleInput}`}
        value={draft}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') { setEditing(false); setDraft(task.title); }
        }}
      />
    );
  }

  return (
    <span className={`${className || ''} ${styles.inlineTitleEditable}`} onClick={startEdit} title="Click to rename">
      {task.title}
      {runningIcon}
      <Pencil size={10} className={styles.inlineTitlePencil} />
    </span>
  );
}

// ─── Task Row (List View) ────────────────────────────────
function TaskRow({ task, project, projects, categories, onOpenDetail, onRunTask, onAssignProject, onDelete, isExecuting, onDragStart }: {
  task: TaskingBotTask;
  project?: TaskingBotProject;
  projects: TaskingBotProject[];
  categories?: { id: string; name: string; position: number }[];
  onOpenDetail: (id: string) => void;
  onRunTask: (id: string, force?: boolean) => void;
  onAssignProject: (taskId: string, projectId: string | null) => void;
  onDelete: (id: string, title: string) => void;
  isExecuting?: boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
}) {
  const meta = parseTaskMeta(task);
  const updateTask = useUpdateTask();
  const hasError = meta.lastRunStatus === 'failed' || meta.lastRunStatus === 'error';
  const isRunning = meta.currentStep && task.status === 'in_progress';
  const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
  const dueDate = task.dueDate || task.endDate;

  // Compute progress from subtasks if available, else from metadata
  const subtasksDone = meta.subtasks.filter(s => s.completed || s.status === 'completed').length;
  const progressPct = meta.subtasks.length > 0
    ? Math.round((subtasksDone / meta.subtasks.length) * 100)
    : task.status === 'completed' ? 100
    : meta.progress ?? (task.status === 'in_progress' ? 50 : 0);

  const cycleStatus = () => {
    const next = task.status === 'open' ? 'in_progress' : task.status === 'in_progress' ? 'completed' : 'open';
    updateTask.mutate({ id: task.id, status: next } as any);
  };

  return (
    <div
      className={`${styles.taskRow} ${hasError ? styles.taskRowError : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
    >
      {/* Top line: Title + Actions */}
      <div className={styles.taskRowTop}>
        <div className={styles.taskRowTitleArea} onClick={() => onOpenDetail(task.id)}>
          <InlineEditableTitle
            task={task}
            className={styles.taskRowTitle}
            runningIcon={isRunning ? <Zap size={12} className={styles.runningIcon} /> : undefined}
          />
        </div>
        <div className={styles.taskRowActions}>
          <ProjectAssignDropdown task={task} projects={projects} categories={categories} onAssign={onAssignProject} />
          {task.status === 'in_progress' && (
            <button className={styles.taskRowRunBtn} onClick={() => onRunTask(task.id, true)} disabled={isExecuting} title="Retry task">
              <RotateCcw size={12} />
            </button>
          )}
          {task.status !== 'completed' && task.status !== 'in_progress' && (
            <button className={styles.taskRowRunBtn} onClick={() => onRunTask(task.id)} disabled={isExecuting} title="Run now">
              <Play size={12} fill="currentColor" />
            </button>
          )}
          <button className={styles.taskRowDeleteBtn} onClick={() => onDelete(task.id, task.title)} title="Delete">
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Progress bar — full width */}
      <div className={styles.taskRowProgressBar}>
        <div
          className={styles.taskRowProgressFill}
          style={{
            width: `${progressPct}%`,
            background: hasError ? '#ef4444' : task.status === 'completed' ? '#22c55e' : '#22c55e',
          }}
        />
      </div>

      {/* Bottom line: Status badge, due date, project, tags, subtask dots */}
      <div className={styles.taskRowBottom}>
        <div className={styles.taskRowMeta}>
          <button className={styles.taskRowBadge} style={{ color: badge.color, background: badge.bg }} onClick={cycleStatus}>
            {badge.label}
          </button>
          {task.priority && task.priority !== 'medium' && (
            <span className={styles.taskRowBadge} style={{ color: PRIORITY_COLORS[task.priority], background: `${PRIORITY_COLORS[task.priority]}18` }}>
              {PRIORITY_LABELS[task.priority]}
            </span>
          )}
          {dueDate && (
            <span className={styles.taskRowDue}>
              <Calendar size={10} />
              {new Date(dueDate).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
            </span>
          )}
          <ProjectTag project={project} size="xs" />
          {parseTags(task).map(tag => (
            <span key={tag} className={styles.tagPill} style={{ borderColor: getTagColor(tag), color: getTagColor(tag) }}>
              <Tag size={8} />{tag}
            </span>
          ))}
          {hasError && meta.lastRunError && (
            <span className={styles.taskRowErrorText}><AlertCircle size={10} /> {meta.lastRunError}</span>
          )}
          {!hasError && isRunning && (
            <span className={styles.taskRowStep}>{meta.currentStep}</span>
          )}
        </div>
        <SubtaskDots subtasks={meta.subtasks} onOpenDetail={onOpenDetail} />
      </div>
    </div>
  );
}

// ─── Kanban Card ──────────────────────────────────────────
function KanbanCard({ task, project, onDragStart, onOpenDetail, onRunTask, isExecuting }: {
  task: TaskingBotTask;
  project?: TaskingBotProject;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onOpenDetail: (id: string) => void;
  onRunTask: (id: string, force?: boolean) => void;
  isExecuting?: boolean;
}) {
  const meta = parseTaskMeta(task);
  const hasError = meta.lastRunStatus === 'failed' || meta.lastRunStatus === 'error';
  const isRunning = meta.currentStep && task.status === 'in_progress';
  const dueDate = task.dueDate || task.endDate;

  return (
    <div
      className={`${styles.kanbanCard} ${hasError ? styles.kanbanCardError : ''}`}
      draggable
      onDragStart={(e) => onDragStart(e, task.id)}
      onClick={() => onOpenDetail(task.id)}
    >
      <InlineEditableTitle task={task} className={styles.kanbanCardTitle} />
      <div className={styles.kanbanCardTags}>
        <ProjectTag project={project} size="xs" />
        {parseTags(task).map(tag => (
          <span key={tag} className={styles.tagPill} style={{ borderColor: getTagColor(tag), color: getTagColor(tag) }}>
            <Tag size={8} />{tag}
          </span>
        ))}
      </div>

      {hasError && meta.lastRunError && (
        <div className={styles.kanbanCardAlert}><AlertCircle size={10} /> {meta.lastRunError}</div>
      )}
      {isRunning && (
        <div className={styles.kanbanCardRunning}><Zap size={10} /> {meta.currentStep}</div>
      )}

      <div className={styles.kanbanCardFooter}>
        <div className={styles.kanbanCardMeta}>
          {task.priority && <span className={styles.kanbanPriority} style={{ color: PRIORITY_COLORS[task.priority] }}>{PRIORITY_LABELS[task.priority]}</span>}
          {dueDate && (
            <span className={styles.kanbanDue}>
              <Calendar size={9} />{new Date(dueDate).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
            </span>
          )}
        </div>
        {task.status === 'in_progress' && (
          <button className={styles.kanbanRunBtn} onClick={(e) => { e.stopPropagation(); onRunTask(task.id, true); }} disabled={isExecuting} title="Retry">
            <RotateCcw size={10} />
          </button>
        )}
        {task.status !== 'completed' && task.status !== 'in_progress' && (
          <button className={styles.kanbanRunBtn} onClick={(e) => { e.stopPropagation(); onRunTask(task.id); }} disabled={isExecuting}>
            <Play size={10} fill="currentColor" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Detailed Row ─────────────────────────────────────────
function DetailedRow({ task, project, projects, categories, onOpenDetail, onRunTask, onAssignProject, onDelete, isExecuting, onDragStart }: {
  task: TaskingBotTask;
  project?: TaskingBotProject;
  projects: TaskingBotProject[];
  categories?: { id: string; name: string; position: number }[];
  onOpenDetail: (id: string) => void;
  onRunTask: (id: string, force?: boolean) => void;
  onAssignProject: (taskId: string, projectId: string | null) => void;
  onDelete: (id: string, title: string) => void;
  isExecuting?: boolean;
  onDragStart: (e: React.DragEvent, id: string) => void;
}) {
  const meta = parseTaskMeta(task);
  const updateTask = useUpdateTask();
  const hasError = meta.lastRunStatus === 'failed' || meta.lastRunStatus === 'error';
  const isRunning = meta.currentStep && task.status === 'in_progress';
  const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
  const dueDate = task.dueDate || task.endDate;
  const startDate = task.startDate;

  const cycleStatus = () => {
    const next = task.status === 'open' ? 'in_progress' : task.status === 'in_progress' ? 'completed' : 'open';
    updateTask.mutate({ id: task.id, status: next } as any);
  };

  let timelinePct = 0;
  if (startDate && dueDate) {
    const s = new Date(startDate).getTime();
    const e = new Date(dueDate).getTime();
    const total = e - s;
    if (total > 0) timelinePct = Math.min(100, Math.max(0, ((Date.now() - s) / total) * 100));
  }
  if (task.status === 'completed') timelinePct = 100;
  const isOverdue = dueDate && new Date(dueDate).toISOString().slice(0, 10) < new Date().toISOString().slice(0, 10) && task.status !== 'completed';

  return (
    <div className={`${styles.detailedRow} ${hasError ? styles.detailedRowError : ''}`} draggable onDragStart={(e) => onDragStart(e, task.id)}>
      <div className={styles.detailedRowTop}>
        <button className={styles.taskRowCheck} onClick={cycleStatus}>
          {task.status === 'completed' ? <CheckCircle2 size={16} style={{ color: '#22c55e' }} /> :
           task.status === 'in_progress' ? <Clock size={16} style={{ color: '#f59e0b' }} /> :
           <Circle size={16} />}
        </button>
        <div className={styles.detailedRowInfo} onClick={() => onOpenDetail(task.id)}>
          <InlineEditableTitle
            task={task}
            className={styles.taskRowTitle}
            runningIcon={isRunning ? <Zap size={12} className={styles.runningIcon} /> : undefined}
          />
          <div className={styles.taskRowSubLine}>
            <ProjectTag project={project} size="xs" />
            {parseTags(task).map(tag => (
              <span key={tag} className={styles.tagPill} style={{ borderColor: getTagColor(tag), color: getTagColor(tag) }}>
                <Tag size={8} />{tag}
              </span>
            ))}
            {task.description && <span className={styles.detailedDesc}>{task.description}</span>}
          </div>
        </div>
        <span className={styles.taskRowBadge} style={{ color: badge.color, background: badge.bg }}>{badge.label}</span>
        {task.priority && <span className={styles.taskRowPriority} style={{ color: PRIORITY_COLORS[task.priority] }}>{PRIORITY_LABELS[task.priority]}</span>}
        <div className={styles.taskRowActions} style={{ opacity: 1 }}>
          <ProjectAssignDropdown task={task} projects={projects} categories={categories} onAssign={onAssignProject} />
          {task.status === 'in_progress' && (
            <button className={styles.taskRowRunBtn} onClick={() => onRunTask(task.id, true)} disabled={isExecuting} title="Retry task">
              <RotateCcw size={12} />
            </button>
          )}
          {task.status !== 'completed' && task.status !== 'in_progress' && (
            <button className={styles.taskRowRunBtn} onClick={() => onRunTask(task.id)} disabled={isExecuting} title="Run now">
              <Play size={12} fill="currentColor" />
            </button>
          )}
          <button className={styles.taskRowDeleteBtn} onClick={() => onDelete(task.id, task.title)}>
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {hasError && meta.lastRunError && (
        <div className={styles.detailedAlert} style={{ color: '#ef4444', background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.2)' }}>
          <AlertCircle size={11} /> {meta.lastRunError}
        </div>
      )}
      {isRunning && (
        <div className={styles.detailedAlert} style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)' }}>
          <Zap size={11} /> {meta.currentStep}
        </div>
      )}
      {meta.blockers.length > 0 && (
        <div className={styles.detailedAlert} style={{ color: '#f59e0b', background: 'rgba(245,158,11,0.08)', borderColor: 'rgba(245,158,11,0.2)' }}>
          <AlertTriangle size={11} /> {meta.blockers.join(', ')}
        </div>
      )}

      <div className={styles.detailedRowBottom}>
        {(startDate || dueDate) && (
          <div className={styles.detailedTimeline}>
            <Calendar size={10} />
            {startDate && <span>{new Date(startDate).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>}
            <div className={styles.detailedTimelineBar}>
              <div className={styles.detailedTimelineFill} style={{
                width: `${timelinePct}%`,
                background: isOverdue ? '#ef4444' : task.status === 'completed' ? '#22c55e' : '#f59e0b',
              }} />
            </div>
            {dueDate && <span style={{ color: isOverdue ? '#ef4444' : undefined }}>{new Date(dueDate).toLocaleDateString('en', { month: 'short', day: 'numeric' })}</span>}
            {isOverdue && <AlertTriangle size={10} style={{ color: '#ef4444' }} />}
          </div>
        )}
        {meta.progress != null && meta.progress > 0 && (
          <div className={styles.detailedProgressWrap}>
            <div className={styles.taskRowProgressBar} style={{ flex: 1, maxWidth: 120 }}>
              <div className={styles.taskRowProgressFill} style={{ width: `${meta.progress}%`, background: hasError ? '#ef4444' : '#22c55e' }} />
            </div>
            <span className={styles.taskRowProgressLabel}>{meta.progress}%</span>
          </div>
        )}
        <div className={styles.detailedRunInfo}>
          {meta.lastRunAt && <span><Timer size={10} /> {timeAgo(meta.lastRunAt)}</span>}
          {meta.runCount != null && meta.runCount > 0 && <span>{meta.runCount} runs</span>}
          {meta.tokensUsed != null && meta.tokensUsed > 0 && <span>{(meta.tokensUsed / 1000).toFixed(1)}k tokens</span>}
          {task.cronExpression && <span style={{ color: '#a78bfa' }}>Recurring</span>}
        </div>
        {meta.lastRunSummary && (
          <div className={styles.detailedSummary} style={{ color: hasError ? '#ef4444' : '#22c55e' }}>{meta.lastRunSummary}</div>
        )}
        <SubtaskDots subtasks={meta.subtasks} onOpenDetail={onOpenDetail} />
      </div>
    </div>
  );
}

// ─── Card View ────────────────────────────────────────────
function TaskCard({ task, project, onOpenDetail, onRunTask, onDelete, isExecuting }: {
  task: TaskingBotTask;
  project?: TaskingBotProject;
  onOpenDetail: (id: string) => void;
  onRunTask: (id: string) => void;
  onDelete: (id: string, title: string) => void;
  isExecuting?: boolean;
}) {
  const meta = parseTaskMeta(task);
  const hasError = meta.lastRunStatus === 'failed' || meta.lastRunStatus === 'error';
  const isRunning = meta.currentStep && task.status === 'in_progress';
  const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
  const dueDate = task.dueDate || task.endDate;
  const progress = meta.progress || 0;

  return (
    <div className={`${styles.card} ${hasError ? styles.cardError : ''}`} onClick={() => onOpenDetail(task.id)}>
      <div className={styles.cardHeader}>
        <span className={styles.cardBadge} style={{ color: badge.color, background: badge.bg }}>{badge.label}</span>
        {task.priority && (
          <span className={styles.cardPriority} style={{ color: PRIORITY_COLORS[task.priority], background: `${PRIORITY_COLORS[task.priority]}14` }}>
            {PRIORITY_LABELS[task.priority]}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <div className={styles.cardActions}>
          {task.status !== 'completed' && (
            <button className={styles.kanbanRunBtn} style={{ opacity: 1 }} onClick={(e) => { e.stopPropagation(); onRunTask(task.id); }} disabled={isExecuting}>
              <Play size={11} fill="currentColor" />
            </button>
          )}
          <button className={styles.taskRowDeleteBtn} onClick={(e) => { e.stopPropagation(); onDelete(task.id, task.title); }}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>

      <InlineEditableTitle
        task={task}
        className={styles.cardTitle}
        runningIcon={isRunning ? <Zap size={12} className={styles.runningIcon} /> : undefined}
      />

      <ProjectTag project={project} size="xs" />

      {task.description && <div className={styles.cardDesc}>{task.description}</div>}

      {hasError && meta.lastRunError && (
        <div className={styles.cardAlert} style={{ color: '#ef4444' }}><AlertCircle size={10} /> {meta.lastRunError}</div>
      )}
      {isRunning && meta.currentStep && (
        <div className={styles.cardAlert} style={{ color: '#f59e0b' }}><Zap size={10} /> {meta.currentStep}</div>
      )}

      {progress > 0 && progress < 100 && (
        <div className={styles.cardProgressWrap}>
          <div className={styles.taskRowProgressBar} style={{ flex: 1 }}>
            <div className={styles.taskRowProgressFill} style={{ width: `${progress}%`, background: hasError ? '#ef4444' : '#22c55e' }} />
          </div>
          <span className={styles.taskRowProgressLabel}>{progress}%</span>
        </div>
      )}

      <div className={styles.cardFooter}>
        {dueDate && (
          <span className={styles.kanbanDue}>
            <Calendar size={9} />{new Date(dueDate).toLocaleDateString('en', { month: 'short', day: 'numeric' })}
          </span>
        )}
        {meta.lastRunAt && <span className={styles.kanbanDue}><Timer size={9} /> {timeAgo(meta.lastRunAt)}</span>}
        {meta.runCount != null && meta.runCount > 0 && <span className={styles.kanbanDue}>{meta.runCount}x</span>}
      </div>
    </div>
  );
}

// ─── Main TaskFlow Component ──────────────────────────────
export type ViewMode = 'list' | 'kanban' | 'detailed' | 'card' | 'schedule';
type ScheduleRange = 'day' | 'week' | 'month';

// ─── Schedule helpers ─────────────────────────────────────
function startOfWeek(d: Date): Date {
  const day = d.getDay();
  const diff = d.getDate() - day;
  return new Date(d.getFullYear(), d.getMonth(), diff);
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function formatDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function getTaskDate(task: TaskingBotTask): string | null {
  return task.dueDate || task.endDate || task.startDate || task.createdAt || null;
}

/** Parse cron expression and generate occurrence dates within a range */
function getCronDates(cronExpr: string, rangeStart: Date, rangeEnd: Date): string[] {
  if (!cronExpr) return [];
  const parts = cronExpr.trim().split(/\s+/);
  if (parts.length < 5) return [];
  const [, , , , dowPart] = parts;
  let daysOfWeek: number[] | null = null;
  if (dowPart !== '*') {
    daysOfWeek = dowPart.split(',').flatMap(p => {
      if (p.includes('-')) {
        const [a, b] = p.split('-').map(Number);
        const result: number[] = [];
        for (let i = a; i <= b; i++) result.push(i % 7);
        return result;
      }
      return [Number(p) % 7];
    });
  }
  const dates: string[] = [];
  const d = new Date(rangeStart);
  while (d <= rangeEnd) {
    if (!daysOfWeek || daysOfWeek.includes(d.getDay())) {
      dates.push(formatDate(d));
    }
    d.setDate(d.getDate() + 1);
  }
  return dates;
}

// ─── Schedule View ────────────────────────────────────────
function ScheduleView({ tasks, projectMap, onOpenDetail }: {
  tasks: TaskingBotTask[];
  projectMap: Map<string, TaskingBotProject>;
  onOpenDetail: (id: string) => void;
}) {
  const [range, setRange] = useState<ScheduleRange>('week');
  const [anchor, setAnchor] = useState(() => new Date());

  const today = new Date();
  const todayStr = formatDate(today);

  // Build date range
  const dates: Date[] = useMemo(() => {
    if (range === 'day') return [anchor];
    if (range === 'week') {
      const start = startOfWeek(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(start, i));
    }
    // month
    const y = anchor.getFullYear();
    const m = anchor.getMonth();
    const first = new Date(y, m, 1);
    const startD = startOfWeek(first);
    // 6 weeks grid
    return Array.from({ length: 42 }, (_, i) => addDays(startD, i));
  }, [range, anchor]);

  // Map tasks to dates — expand recurring tasks across visible range
  const tasksByDate = useMemo(() => {
    const map = new Map<string, TaskingBotTask[]>();
    const rangeStart = dates[0];
    const rangeEnd = dates[dates.length - 1];

    for (const task of tasks) {
      // Recurring tasks: expand cron across visible range
      if (task.cronExpression && task.status !== 'completed' && task.status !== 'cancelled') {
        const cronDates = getCronDates(task.cronExpression, rangeStart, rangeEnd);
        for (const key of cronDates) {
          if (!map.has(key)) map.set(key, []);
          if (!map.get(key)!.some(t => t.id === task.id)) {
            map.get(key)!.push(task);
          }
        }
        continue;
      }
      // Non-recurring: single date
      const dateStr = getTaskDate(task);
      if (!dateStr) continue;
      const key = dateStr.slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(task);
    }
    return map;
  }, [tasks, dates]);

  // Tasks without dates and without cron
  const undatedTasks = useMemo(() => tasks.filter(t => !getTaskDate(t) && !t.cronExpression), [tasks]);

  const navigate = (dir: -1 | 1) => {
    if (range === 'day') setAnchor(addDays(anchor, dir));
    else if (range === 'week') setAnchor(addDays(anchor, dir * 7));
    else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + dir, 1));
  };

  const headerLabel = range === 'day'
    ? anchor.toLocaleDateString('en', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
    : range === 'week'
      ? `${dates[0].toLocaleDateString('en', { month: 'short', day: 'numeric' })} – ${dates[6].toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' })}`
      : anchor.toLocaleDateString('en', { month: 'long', year: 'numeric' });

  const isCurrentMonth = (d: Date) => d.getMonth() === anchor.getMonth();

  return (
    <div className={styles.scheduleWrap}>
      {/* Schedule header */}
      <div className={styles.scheduleHeader}>
        <div className={styles.scheduleNav}>
          <button className={styles.scheduleNavBtn} onClick={() => navigate(-1)}><ChevronLeft size={14} /></button>
          <span className={styles.scheduleLabel}>{headerLabel}</span>
          <button className={styles.scheduleNavBtn} onClick={() => navigate(1)}><ChevronRight size={14} /></button>
          <button className={styles.scheduleTodayBtn} onClick={() => setAnchor(new Date())}>Today</button>
        </div>
        <div className={styles.scheduleRangeToggle}>
          {(['day', 'week', 'month'] as ScheduleRange[]).map(r => (
            <button
              key={r}
              className={`${styles.scheduleRangeBtn} ${range === r ? styles.scheduleRangeBtnActive : ''}`}
              onClick={() => setRange(r)}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Day view */}
      {range === 'day' && (
        <div className={styles.scheduleDayView}>
          {(() => {
            const dayTasks = tasksByDate.get(formatDate(anchor)) || [];
            return dayTasks.length === 0 ? (
              <div className={styles.empty}>No tasks scheduled for this day.</div>
            ) : (
              <div className={styles.scheduleDayTasks}>
                {dayTasks.map(task => {
                  const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                  const project = task.projectId ? projectMap.get(task.projectId) : undefined;
                  return (
                    <div key={task.id} className={styles.scheduleTask} onClick={() => onOpenDetail(task.id)}>
                      <span className={styles.scheduleTaskDot} style={{ background: badge.color }} />
                      <span className={styles.scheduleTaskTitle}>{task.title}</span>
                      {project && <ProjectTag project={project} size="xs" />}
                      {task.priority && (
                        <span style={{ color: PRIORITY_COLORS[task.priority], fontSize: 9, fontWeight: 700 }}>
                          {PRIORITY_LABELS[task.priority]}
                        </span>
                      )}
                      <span className={styles.taskRowBadge} style={{ color: badge.color, background: badge.bg, marginLeft: 'auto' }}>
                        {badge.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* Week view */}
      {range === 'week' && (
        <div className={styles.scheduleWeekGrid}>
          {dates.map(date => {
            const key = formatDate(date);
            const dayTasks = tasksByDate.get(key) || [];
            const isToday = key === todayStr;
            return (
              <div key={key} className={`${styles.scheduleWeekDay} ${isToday ? styles.scheduleWeekDayToday : ''}`}>
                <div className={styles.scheduleWeekDayHeader}>
                  <span className={styles.scheduleWeekDayName}>{date.toLocaleDateString('en', { weekday: 'short' })}</span>
                  <span className={`${styles.scheduleWeekDayNum} ${isToday ? styles.scheduleWeekDayNumToday : ''}`}>
                    {date.getDate()}
                  </span>
                </div>
                <div className={styles.scheduleWeekDayBody}>
                  {dayTasks.map(task => {
                    const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                    return (
                      <div key={task.id} className={styles.scheduleWeekTask} onClick={() => onOpenDetail(task.id)} style={{ borderLeftColor: badge.color }}>
                        <span className={styles.scheduleWeekTaskTitle}>{task.title}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Month view */}
      {range === 'month' && (
        <>
          <div className={styles.scheduleMonthDayNames}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
              <span key={d} className={styles.scheduleMonthDayName}>{d}</span>
            ))}
          </div>
          <div className={styles.scheduleMonthGrid}>
            {dates.map(date => {
              const key = formatDate(date);
              const dayTasks = tasksByDate.get(key) || [];
              const isToday = key === todayStr;
              const inMonth = isCurrentMonth(date);
              return (
                <div key={key} className={`${styles.scheduleMonthCell} ${!inMonth ? styles.scheduleMonthCellDim : ''} ${isToday ? styles.scheduleMonthCellToday : ''}`}>
                  <span className={styles.scheduleMonthCellNum}>{date.getDate()}</span>
                  {dayTasks.slice(0, 3).map(task => {
                    const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
                    return (
                      <div key={task.id} className={styles.scheduleMonthTask} onClick={() => onOpenDetail(task.id)} style={{ background: badge.bg, color: badge.color }}>
                        {task.title}
                      </div>
                    );
                  })}
                  {dayTasks.length > 3 && (
                    <span className={styles.scheduleMonthMore}>+{dayTasks.length - 3} more</span>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Undated tasks */}
      {undatedTasks.length > 0 && (
        <div className={styles.scheduleUndated}>
          <span className={styles.scheduleUndatedLabel}>No date ({undatedTasks.length})</span>
          <div className={styles.scheduleUndatedList}>
            {undatedTasks.slice(0, 10).map(task => {
              const badge = STATUS_BADGE[task.status] || STATUS_BADGE.open;
              return (
                <div key={task.id} className={styles.scheduleTask} onClick={() => onOpenDetail(task.id)}>
                  <span className={styles.scheduleTaskDot} style={{ background: badge.color }} />
                  <span className={styles.scheduleTaskTitle}>{task.title}</span>
                </div>
              );
            })}
            {undatedTasks.length > 10 && <span className={styles.scheduleMonthMore}>+{undatedTasks.length - 10} more</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export function TaskFlow({ projectId }: { projectId?: string | null }) {
  const filters: Record<string, string> = {};
  if (projectId) filters.projectId = projectId;
  const { data: tasks, isLoading } = useTaskingBotTasks(
    Object.keys(filters).length > 0 ? filters : undefined,
  );
  const { data: projects } = useTaskingBotProjects();
  const { data: categories } = useTaskingBotCategories();
  const updateTask = useUpdateTask();
  const createTask = useCreateTaskingBotTask();
  const executeTask = useExecuteTaskingBotTaskNow();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const createCategory = useCreateCategory();
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [draggedFolderId, setDraggedFolderId] = useState<string | null>(null);
  const [detailTaskId, setDetailTaskId] = useState<string | null>(null);
  const [executingTaskId, setExecutingTaskId] = useState<string | null>(null);
  const deleteTask = useDeleteTask();
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; title: string } | null>(null);

  // Folder CRUD state
  const [addingFolderParent, setAddingFolderParent] = useState<string | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editFolderName, setEditFolderName] = useState('');
  const folderInputRef = useRef<HTMLInputElement>(null);
  const editFolderRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (addingFolderParent !== null && folderInputRef.current) folderInputRef.current.focus();
  }, [addingFolderParent]);
  useEffect(() => {
    if (editingFolderId && editFolderRef.current) editFolderRef.current.focus();
  }, [editingFolderId]);

  const defaultCategoryId = (categories || [])[0]?.id || '';

  const handleCreateFolder = useCallback(async (parentProjectId?: string | null) => {
    const name = newFolderName.trim();
    if (!name) { setAddingFolderParent(null); return; }
    try {
      let catId = defaultCategoryId;
      // Inherit categoryId from parent folder if available
      if (parentProjectId) {
        const parent = (projects || []).find((p: any) => p.id === parentProjectId);
        if (parent && (parent as any).categoryId) catId = (parent as any).categoryId;
      }
      // Ensure we have a valid categoryId — create default category if needed
      if (!catId) {
        const catResult = await createCategory.mutateAsync({ name: 'Projects' });
        catId = (catResult as any)?.category?.id || (catResult as any)?.id || '';
      }
      if (!catId) {
        toast.error('Could not resolve a category for this folder');
        return;
      }
      await createProject.mutateAsync({ name, categoryId: catId, parentProjectId: parentProjectId || undefined });
      setNewFolderName('');
      setAddingFolderParent(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create folder');
    }
  }, [newFolderName, defaultCategoryId, projects, createProject, createCategory]);

  const handleRenameFolder = useCallback(async (projId: string) => {
    const name = editFolderName.trim();
    if (!name) { setEditingFolderId(null); return; }
    try {
      await updateProject.mutateAsync({ projectId: projId, name });
      setEditingFolderId(null);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to rename folder');
    }
  }, [editFolderName, updateProject]);

  // handleDeleteFolder is defined after allTasks below (needs it in scope)
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickTitle, setQuickTitle] = useState('');
  const [quickPriority, setQuickPriority] = useState<string>('medium');
  const [quickProjectId, setQuickProjectId] = useState<string>(projectId || '');
  const [quickSchedule, setQuickSchedule] = useState(false);
  const [quickDueDate, setQuickDueDate] = useState('');
  const quickInputRef = useRef<HTMLInputElement>(null);
  const [filterProjectId, setFilterProjectId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterTag, setFilterTag] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try { return (localStorage.getItem('taskflow-view') as ViewMode) || 'list'; } catch { return 'list'; }
  });

  const projectMap = useMemo(() => {
    const map = new Map<string, TaskingBotProject>();
    (projects || []).forEach(p => map.set(p.id, p));
    return map;
  }, [projects]);

  const allTags = useMemo(() => {
    const tagSet = new Set<string>();
    (tasks || []).forEach(t => parseTags(t).forEach(tag => tagSet.add(tag)));
    return Array.from(tagSet).sort();
  }, [tasks]);

  const allTasks = useMemo(() => {
    let filtered = (tasks || []).filter(
      (task) => !task.parentTaskId || !(tasks || []).some((c) => c.id === task.parentTaskId),
    );
    if (filterProjectId) filtered = filtered.filter(t => t.projectId === filterProjectId);
    if (filterStatus === 'recent') {
      const cutoff = Date.now() - 24 * 60 * 60 * 1000;
      filtered = filtered.filter(t => t.updatedAt && new Date(t.updatedAt).getTime() > cutoff);
      filtered.sort((a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime());
    } else if (filterStatus === 'planned') {
      // Planned = has pipeline metadata or subtasks, not completed
      filtered = filtered.filter(t => {
        const meta = typeof t.metadata === 'string' ? (() => { try { return JSON.parse(t.metadata); } catch { return {}; } })() : (t.metadata || {});
        return (meta.pipeline || (Array.isArray(meta.subtasks) && meta.subtasks.length > 0)) && t.status !== 'completed';
      });
    } else if (filterStatus === 'scheduled') {
      // Scheduled = has cron expression or future due date
      filtered = filtered.filter(t => {
        return Boolean(t.cronExpression) || (t.dueDate && new Date(t.dueDate) >= new Date());
      });
    } else if (filterStatus) {
      filtered = filtered.filter(t => t.status === filterStatus);
    }
    if (filterTag) filtered = filtered.filter(t => parseTags(t).includes(filterTag));
    return filtered;
  }, [tasks, filterProjectId, filterStatus, filterTag]);

  const handleDeleteFolder = useCallback(async (proj: any) => {
    const folderTasks = (allTasks || []).filter((t: any) => t.projectId === proj.id);
    if (folderTasks.length > 0) {
      toast.error(`Move ${folderTasks.length} task(s) out of "${proj.name}" first`);
      return;
    }
    if (!window.confirm(`Delete folder "${proj.name}"?`)) return;
    try {
      await deleteProject.mutateAsync(proj.id);
      toast.success(`Deleted "${proj.name}"`);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to delete folder');
    }
  }, [allTasks, deleteProject]);

  // Folder grouping: group tasks by project when no project filter is active
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>({});
  const showFolderView = !filterProjectId && !projectId;

  const projectFolders = useMemo(() => {
    if (!showFolderView) return [];
    const projList = (projects || []) as any[];
    const tasksByProj = new Map<string | null, TaskingBotTask[]>();
    for (const t of allTasks) {
      const key = t.projectId || null;
      const arr = tasksByProj.get(key) || [];
      arr.push(t);
      tasksByProj.set(key, arr);
    }
    type FolderItem = { id: string; name: string; color?: string; tasks: TaskingBotTask[]; depth: number; project: any };
    const folders: FolderItem[] = [];

    // Build parent→children map
    const byParent = new Map<string | null, any[]>();
    for (const proj of projList) {
      const parentId = proj.parentProjectId && projList.some((p: any) => p.id === proj.parentProjectId)
        ? proj.parentProjectId
        : null;
      const bucket = byParent.get(parentId) || [];
      bucket.push(proj);
      byParent.set(parentId, bucket);
    }
    for (const bucket of byParent.values()) {
      bucket.sort((a: any, b: any) => a.position - b.position || a.name.localeCompare(b.name));
    }

    // Walk tree depth-first — show ALL folders (even empty ones for hierarchy)
    const seen = new Set<string>();
    const walk = (parentId: string | null, depth: number) => {
      const children = byParent.get(parentId) || [];
      for (const proj of children) {
        if (seen.has(proj.id)) continue;
        seen.add(proj.id);
        folders.push({ id: proj.id, name: proj.name, color: proj.color, tasks: tasksByProj.get(proj.id) || [], depth, project: proj });
        walk(proj.id, depth + 1);
      }
    };
    walk(null, 0);

    // Orphaned projects
    for (const proj of projList) {
      if (!seen.has(proj.id)) {
        folders.push({ id: proj.id, name: proj.name, color: proj.color, tasks: tasksByProj.get(proj.id) || [], depth: 0, project: proj });
      }
    }

    // Ungrouped tasks (no project)
    const ungrouped = tasksByProj.get(null);
    if (ungrouped && ungrouped.length > 0) {
      folders.push({ id: '__ungrouped', name: 'Ungrouped Tasks', tasks: ungrouped, depth: 0, project: null });
    }
    return folders;
  }, [showFolderView, allTasks, projects]);

  // Sync quick-add project with sidebar selection
  useEffect(() => {
    setQuickProjectId(projectId || '');
  }, [projectId]);

  useEffect(() => {
    if (showQuickAdd) {
      // Default to selected project when opening quick add
      if (!quickProjectId && projectId) setQuickProjectId(projectId);
      quickInputRef.current?.focus();
    }
  }, [showQuickAdd]);

  const handleQuickAdd = useCallback(async (andExecute = false) => {
    const title = quickTitle.trim();
    if (!title) return;
    try {
      const payload: any = {
        title,
        priority: quickPriority as any,
      };
      if (quickProjectId) payload.projectId = quickProjectId;
      if (projectId) payload.projectId = payload.projectId || projectId;
      if (quickDueDate) payload.description = `Due: ${quickDueDate}`;
      const result = await createTask.mutateAsync(payload);
      if (andExecute && result?.id) {
        await executeTask.mutateAsync(result.id);
        toast.success('Task created & started');
      } else {
        toast.success('Task created');
      }
      setQuickTitle('');
      setQuickPriority('medium');
      setQuickProjectId('');
      setQuickDueDate('');
      setShowQuickAdd(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create task');
    }
  }, [quickTitle, quickPriority, quickProjectId, quickDueDate, projectId, createTask, executeTask]);

  const handleRunTask = useCallback(async (taskId: string, force?: boolean) => {
    setExecutingTaskId(taskId);
    try {
      await executeTask.mutateAsync(force ? { taskId, force: true } : taskId);
      toast.success(force ? 'Task retried' : 'Task started');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to run task');
    } finally {
      setExecutingTaskId(null);
    }
  }, [executeTask]);

  const handleAssignProject = useCallback((taskId: string, projId: string | null) => {
    updateTask.mutate({ id: taskId, projectId: projId } as any, {
      onSuccess: () => toast.success(projId ? 'Assigned to project' : 'Removed from project'),
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to assign'),
    });
  }, [updateTask]);

  const handleDragStart = useCallback((e: React.DragEvent, taskId: string) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', taskId);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData('text/plain') || draggedTaskId;
    if (!taskId) return;
    const task = allTasks.find(t => t.id === taskId);
    if (!task || task.status === targetStatus) return;
    updateTask.mutate({ id: taskId, status: targetStatus } as any, {
      onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update task'),
    });
    setDraggedTaskId(null);
  }, [draggedTaskId, allTasks, updateTask]);

  const handleFolderDragStart = useCallback((e: React.DragEvent, folderId: string) => {
    e.stopPropagation();
    setDraggedFolderId(folderId);
    setDraggedTaskId(null);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/x-folder-id', folderId);
  }, []);

  const isDescendantOf = useCallback((folderId: string, targetParentId: string): boolean => {
    const projList = (projects || []) as any[];
    let current = targetParentId;
    const visited = new Set<string>();
    while (current) {
      if (current === folderId) return true;
      if (visited.has(current)) break;
      visited.add(current);
      const parent = projList.find((p: any) => p.id === current);
      current = parent?.parentProjectId || null;
    }
    return false;
  }, [projects]);

  const handleFolderDrop = useCallback((e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove(styles.folderRowDragOver);
    const folderId = e.dataTransfer.getData('application/x-folder-id') || draggedFolderId;
    // If a task is being dragged, handle task drop to folder
    if (!folderId && draggedTaskId) {
      const newProjectId = targetFolderId === '__ungrouped' ? null : targetFolderId;
      updateTask.mutate({ id: draggedTaskId, projectId: newProjectId } as any);
      toast.success('Moved task');
      setDraggedTaskId(null);
      return;
    }
    if (!folderId || folderId === targetFolderId) { setDraggedFolderId(null); return; }
    if (folderId === '__ungrouped') { setDraggedFolderId(null); return; }
    // Prevent dropping a folder into its own descendant
    if (targetFolderId && isDescendantOf(folderId, targetFolderId)) {
      toast.error('Cannot move a folder into its own subfolder');
      setDraggedFolderId(null);
      return;
    }
    // Include categoryId from target parent so same-category check passes
    const targetProj = targetFolderId ? (projects || []).find((p: any) => p.id === targetFolderId) : null;
    const movePayload: any = { projectId: folderId, parentProjectId: targetFolderId || null };
    if (targetProj && (targetProj as any).categoryId) {
      movePayload.categoryId = (targetProj as any).categoryId;
    }
    updateProject.mutate(movePayload, {
      onSuccess: () => toast.success(targetFolderId ? 'Moved into folder' : 'Moved to root'),
      onError: (err: any) => toast.error(err?.message || 'Failed to move folder'),
    });
    setDraggedFolderId(null);
  }, [draggedFolderId, draggedTaskId, updateTask, updateProject, isDescendantOf]);

  const toggleView = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    try { localStorage.setItem('taskflow-view', mode); } catch {}
  }, []);

  const hasActiveFilters = filterProjectId || filterStatus || filterTag;
  const projectsList = projects || [];

  // Group projects by category to match sidebar folder structure
  const categoriesList = categories || [];
  const projectsByCategory = useMemo(() => {
    const map = new Map<string, TaskingBotProject[]>();
    for (const p of projectsList) {
      const catId = (p as any).categoryId || '__uncategorized';
      const bucket = map.get(catId) || [];
      bucket.push(p);
      map.set(catId, bucket);
    }
    // Sort projects within each category by position
    for (const bucket of map.values()) {
      bucket.sort((a, b) => a.position - b.position || a.name.localeCompare(b.name));
    }
    return map;
  }, [projectsList]);
  const uncategorizedProjects = projectsByCategory.get('__uncategorized') || [];

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Activity size={20} className={styles.spin} />
        <span>Loading tasks...</span>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Stats Bar */}
      <StatsBar tasks={tasks || []} />

      {/* Header + View Toggle + Filters */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <h3 className={styles.sectionTitle}>Tasks</h3>
          <span className={styles.taskCount}>{allTasks.length}</span>
          <button
            className={`${styles.quickAddBtn} ${showQuickAdd ? styles.quickAddBtnActive : ''}`}
            onClick={() => setShowQuickAdd(!showQuickAdd)}
            title="Add task"
          >
            <Plus size={14} />
          </button>
        </div>

        <div className={styles.toolbarCenter}>
          {/* Project filter */}
          <div className={styles.filterGroup}>
            <select
              className={styles.filterSelect}
              value={filterProjectId || ''}
              onChange={(e) => setFilterProjectId(e.target.value || null)}
            >
              <option value="">All Projects</option>
              {categoriesList.sort((a, b) => a.position - b.position).map(cat => {
                const catProjects = projectsByCategory.get(cat.id) || [];
                if (catProjects.length === 0) return null;
                return (
                  <optgroup key={cat.id} label={cat.name}>
                    {catProjects.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </optgroup>
                );
              })}
              {uncategorizedProjects.length > 0 && (
                <optgroup label="Other">
                  {uncategorizedProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
            <select
              className={styles.filterSelect}
              value={filterStatus || ''}
              onChange={(e) => setFilterStatus(e.target.value || null)}
            >
              <option value="">All Status</option>
              <option value="recent">Recent (24h)</option>
              <option value="open">To Do</option>
              <option value="in_progress">In Progress</option>
              <option value="planned">Planned</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Done</option>
            </select>
            {allTags.length > 0 && (
              <select
                className={styles.filterSelect}
                value={filterTag || ''}
                onChange={(e) => setFilterTag(e.target.value || null)}
              >
                <option value="">All Tags</option>
                {allTags.map(tag => (
                  <option key={tag} value={tag}>{tag}</option>
                ))}
              </select>
            )}
            {hasActiveFilters && (
              <button className={styles.filterClear} onClick={() => { setFilterProjectId(null); setFilterStatus(null); setFilterTag(null); }}>
                <X size={12} /> Clear
              </button>
            )}
          </div>
        </div>

        <div className={styles.viewToggle}>
          {([
            { mode: 'list' as ViewMode, icon: LayoutList, label: 'List' },
            { mode: 'kanban' as ViewMode, icon: LayoutGrid, label: 'Board' },
            { mode: 'detailed' as ViewMode, icon: Rows3, label: 'Detail' },
            { mode: 'card' as ViewMode, icon: CreditCard, label: 'Cards' },
            { mode: 'schedule' as ViewMode, icon: CalendarDays, label: 'Schedule' },
          ]).map(({ mode, icon: Icon, label }) => (
            <button
              key={mode}
              className={`${styles.viewToggleBtn} ${viewMode === mode ? styles.viewToggleBtnActive : ''}`}
              onClick={() => toggleView(mode)}
              title={label}
            >
              <Icon size={14} />
            </button>
          ))}
        </div>
      </div>

      {/* Quick Add Form */}
      {showQuickAdd && (
        <div className={styles.quickAddForm}>
          <input
            ref={quickInputRef}
            className={styles.quickAddInput}
            type="text"
            placeholder="Task title..."
            value={quickTitle}
            onChange={(e) => setQuickTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleQuickAdd(); }
              if (e.key === 'Escape') setShowQuickAdd(false);
            }}
          />
          <div className={styles.quickAddOptions}>
            <select className={styles.quickAddSelect} value={quickPriority} onChange={(e) => setQuickPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
            {projectsList.length > 0 && (
              <select className={styles.quickAddSelect} value={quickProjectId} onChange={(e) => setQuickProjectId(e.target.value)}>
                <option value="">No project</option>
                {categoriesList.sort((a, b) => a.position - b.position).map(cat => {
                  const catProjects = projectsByCategory.get(cat.id) || [];
                  if (catProjects.length === 0) return null;
                  return (
                    <optgroup key={cat.id} label={cat.name}>
                      {catProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </optgroup>
                  );
                })}
                {uncategorizedProjects.length > 0 && (
                  <optgroup label="Other">
                    {uncategorizedProjects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                )}
              </select>
            )}
            <input
              className={styles.quickAddDate}
              type="date"
              value={quickDueDate}
              onChange={(e) => setQuickDueDate(e.target.value)}
              title="Due date"
            />
          </div>
          <div className={styles.quickAddActions}>
            <button
              className={styles.quickAddSubmit}
              onClick={() => handleQuickAdd(false)}
              disabled={!quickTitle.trim() || createTask.isPending}
            >
              <Plus size={12} /> Create
            </button>
            <button
              className={styles.quickAddExecute}
              onClick={() => handleQuickAdd(true)}
              disabled={!quickTitle.trim() || createTask.isPending}
              title="Create and immediately execute"
            >
              <Play size={12} /> Create & Run
            </button>
            <button className={styles.quickAddCancel} onClick={() => setShowQuickAdd(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className={styles.taskList}>
          {allTasks.length === 0 && !showFolderView ? (
            <div className={styles.empty}>No tasks{hasActiveFilters ? ' match filters' : ''}.</div>
          ) : showFolderView ? (
            <>
              {/* Folder toolbar */}
              <div className={styles.folderToolbar}>
                <span className={styles.folderToolbarTitle}>
                  <FolderOpen size={13} />
                  Folders
                </span>
                <button
                  className={styles.folderAddBtn}
                  onClick={() => { setAddingFolderParent('__root'); setNewFolderName(''); }}
                  title="Create folder"
                >
                  <FolderPlus size={13} />
                </button>
              </div>

              {/* Inline add at top level */}
              {addingFolderParent === '__root' && (
                <div className={styles.folderInlineAdd}>
                  <input
                    ref={folderInputRef}
                    className={styles.folderInlineInput}
                    placeholder="Folder name..."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleCreateFolder(null);
                      if (e.key === 'Escape') { setAddingFolderParent(null); setNewFolderName(''); }
                    }}
                    onBlur={() => handleCreateFolder(null)}
                  />
                </div>
              )}

              {projectFolders.map(folder => {
                const isOpen = collapsedFolders[folder.id] !== true;
                const FIcon = isOpen ? FolderOpen : FolderClosed;
                const isEditing = editingFolderId === folder.id;
                return (
                  <div key={folder.id} style={{ marginBottom: 2 }}>
                    <div
                      className={`${styles.folderRow} ${draggedFolderId === folder.id ? styles.folderRowDragging : ''}`}
                      style={{ paddingLeft: `${8 + (folder.depth || 0) * 16}px` }}
                      draggable={folder.id !== '__ungrouped'}
                      onDragStart={(e) => handleFolderDragStart(e, folder.id)}
                      onDragEnd={() => setDraggedFolderId(null)}
                      onClick={() => setCollapsedFolders(prev => ({ ...prev, [folder.id]: isOpen }))}
                      onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add(styles.folderRowDragOver); }}
                      onDragLeave={(e) => e.currentTarget.classList.remove(styles.folderRowDragOver)}
                      onDrop={(e) => handleFolderDrop(e, folder.id)}
                    >
                      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      <FIcon size={14} style={{ opacity: 0.7 }} />
                      {folder.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: folder.color, flexShrink: 0 }} />}
                      {isEditing && folder.project ? (
                        <input
                          ref={editFolderRef}
                          className={styles.folderInlineInput}
                          value={editFolderName}
                          onChange={(e) => setEditFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleRenameFolder(folder.id);
                            if (e.key === 'Escape') setEditingFolderId(null);
                          }}
                          onBlur={() => handleRenameFolder(folder.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      ) : (
                        <span style={{ fontWeight: 600, fontSize: '0.82rem', flex: 1 }}>{folder.name}</span>
                      )}
                      <span style={{ fontSize: '0.72rem', color: 'var(--muted-foreground)', opacity: 0.7 }}>{folder.tasks.length}</span>
                      {/* Folder actions */}
                      {folder.project && (
                        <span className={styles.folderActions}>
                          <button
                            className={styles.folderActionBtn}
                            onClick={(e) => { e.stopPropagation(); setAddingFolderParent(folder.id); setNewFolderName(''); }}
                            title="Add child folder"
                          >
                            <Plus size={11} />
                          </button>
                          <button
                            className={styles.folderActionBtn}
                            onClick={(e) => { e.stopPropagation(); setEditingFolderId(folder.id); setEditFolderName(folder.name); }}
                            title="Rename"
                          >
                            <Pencil size={11} />
                          </button>
                          <button
                            className={styles.folderActionBtn}
                            onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.project); }}
                            title="Delete"
                          >
                            <Trash2 size={11} />
                          </button>
                        </span>
                      )}
                    </div>

                    {/* Inline add child folder */}
                    {addingFolderParent === folder.id && (
                      <div className={styles.folderInlineAdd} style={{ paddingLeft: `${24 + (folder.depth || 0) * 16}px` }}>
                        <input
                          ref={folderInputRef}
                          className={styles.folderInlineInput}
                          placeholder="Child folder name..."
                          value={newFolderName}
                          onChange={(e) => setNewFolderName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleCreateFolder(folder.id);
                            if (e.key === 'Escape') { setAddingFolderParent(null); setNewFolderName(''); }
                          }}
                          onBlur={() => handleCreateFolder(folder.id)}
                        />
                      </div>
                    )}

                    {isOpen && folder.tasks.map(task => (
                      <TaskRow
                        key={task.id} task={task}
                        project={task.projectId ? projectMap.get(task.projectId) : undefined}
                        projects={projectsList}
                        categories={categoriesList}
                        onOpenDetail={setDetailTaskId}
                        onRunTask={handleRunTask}
                        onAssignProject={handleAssignProject}
                        onDelete={(id, title) => setConfirmDelete({ id, title })}
                        isExecuting={executingTaskId === task.id}
                        onDragStart={handleDragStart}
                      />
                    ))}
                  </div>
                );
              })}

              {projectFolders.length === 0 && allTasks.length === 0 && (
                <div className={styles.empty}>No folders yet. Click <FolderPlus size={12} style={{ verticalAlign: 'middle' }} /> to create one.</div>
              )}
            </>
          ) : allTasks.map(task => (
            <TaskRow
              key={task.id} task={task}
              project={task.projectId ? projectMap.get(task.projectId) : undefined}
              projects={projectsList}
              categories={categoriesList}
              onOpenDetail={setDetailTaskId}
              onRunTask={handleRunTask}
              onAssignProject={handleAssignProject}
              onDelete={(id, title) => setConfirmDelete({ id, title })}
              isExecuting={executingTaskId === task.id}
              onDragStart={handleDragStart}
            />
          ))}
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className={styles.kanbanGrid}>
          {COLUMNS.map(col => {
            const colTasks = allTasks.filter(t => t.status === col.key);
            return (
              <div key={col.key} className={styles.kanbanColumn} onDragOver={handleDragOver} onDrop={(e) => handleDrop(e, col.key)}>
                <div className={styles.kanbanColumnHeader} style={{ borderColor: col.color }}>
                  <span className={styles.kanbanColumnDot} style={{ background: col.dotColor }} />
                  <span className={styles.kanbanColumnLabel}>{col.label}</span>
                  <span className={styles.kanbanColumnCount} style={{ color: col.color, background: `${col.color}1a` }}>{colTasks.length}</span>
                </div>
                <div className={styles.kanbanColumnBody}>
                  {colTasks.length === 0 ? (
                    <div className={styles.kanbanEmpty}>Drop tasks here</div>
                  ) : colTasks.map(task => (
                    <KanbanCard
                      key={task.id} task={task}
                      project={task.projectId ? projectMap.get(task.projectId) : undefined}
                      onDragStart={handleDragStart}
                      onOpenDetail={setDetailTaskId}
                      onRunTask={handleRunTask}
                      isExecuting={executingTaskId === task.id}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Detailed View */}
      {viewMode === 'detailed' && (
        <div className={styles.taskList}>
          {allTasks.length === 0 ? (
            <div className={styles.empty}>No tasks{hasActiveFilters ? ' match filters' : ''}.</div>
          ) : allTasks.map(task => (
            <DetailedRow
              key={task.id} task={task}
              project={task.projectId ? projectMap.get(task.projectId) : undefined}
              projects={projectsList}
              categories={categoriesList}
              onOpenDetail={setDetailTaskId}
              onRunTask={handleRunTask}
              onAssignProject={handleAssignProject}
              onDelete={(id, title) => setConfirmDelete({ id, title })}
              isExecuting={executingTaskId === task.id}
              onDragStart={handleDragStart}
            />
          ))}
        </div>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <div className={styles.cardGrid}>
          {allTasks.length === 0 ? (
            <div className={styles.empty}>No tasks{hasActiveFilters ? ' match filters' : ''}.</div>
          ) : allTasks.map(task => (
            <TaskCard
              key={task.id} task={task}
              project={task.projectId ? projectMap.get(task.projectId) : undefined}
              onOpenDetail={setDetailTaskId}
              onRunTask={handleRunTask}
              onDelete={(id, title) => setConfirmDelete({ id, title })}
              isExecuting={executingTaskId === task.id}
            />
          ))}
        </div>
      )}

      {/* Schedule View */}
      {viewMode === 'schedule' && (
        <ScheduleView
          tasks={allTasks}
          projectMap={projectMap}
          onOpenDetail={setDetailTaskId}
        />
      )}

      {/* Task detail modal */}
      {detailTaskId && (
        <TaskDetailModal
          taskId={detailTaskId}
          open={!!detailTaskId}
          onOpenChange={(open) => { if (!open) setDetailTaskId(null); }}
        />
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <ConfirmDialog
          title="Delete Task"
          message={`Are you sure you want to delete "${confirmDelete.title}"? This action cannot be undone.`}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={() => {
            deleteTask.mutate(confirmDelete.id, {
              onSuccess: () => toast.success('Task deleted'),
              onError: (err) => toast.error(err instanceof Error ? err.message : 'Delete failed'),
            });
            setConfirmDelete(null);
          }}
        />
      )}
    </div>
  );
}
